// ** React Imports
import { useState, useEffect } from 'react'

// ** Next Imports

// ** MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import Tooltip from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import MuiTabList from '@mui/lab/TabList'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Styled Components
import CustomHeader from 'src/@core/components/Header'
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useMediaQuery
} from '@mui/material'
import { green, red } from '@mui/material/colors'
import { TabContext, TabPanel } from '@mui/lab'
import { Clear, Refresh, Save } from '@mui/icons-material'
import { construirEnlace, leerEstadoDeUrl } from 'src/@core/utils/enlace-compartible'

/*
  Estado compartible. mtData guarda las herramientas enteras —incluida `conf`, que
  son tablas fijas como los tiers de angel— y lo unico que el jugador decide es
  `info`. Se codifica solo eso, indexado por la clave de cada herramienta para que
  añadir una nueva no corra las viejas.
*/
const aEstadoMinimo = datos =>
  Object.fromEntries((datos ?? []).map(d => [d.key, d.info]))

const desdeEstadoMinimo = minimo => {
  if (!minimo || typeof minimo !== 'object') return null
  let alguno = false

  const datos = infoData.map(d => {
    const info = minimo[d.key]
    if (!info || typeof info !== 'object') return { ...d }
    alguno = true

    // Solo las claves que la herramienta ya tiene, y solo numeros
    const limpio = Object.fromEntries(Object.keys(d.info ?? {}).map(k => [k, Number(info[k]) || 0]))

    return { ...d, info: { ...d.info, ...limpio } }
  })

  return alguno ? datos : null
}

// ** Styled component
const CustomBox = styled(Box)(({ theme }) => ({
  marginTop: '4px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexDirection: 'row'
}))

const GreenButton = styled(Button)(({ theme }) => ({
  backgroundColor: green[500],
  color: theme.palette.getContrastText(green[500]),
  '&:hover': {
    backgroundColor: green[700]
  }
}))

const RedButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(red[700]),
  backgroundColor: red[700],
  '&:hover': {
    backgroundColor: red[700]
  }
}))

const TabList = styled(MuiTabList)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    display: 'none'
  },
  '& .Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    // contrastText: en modo oscuro el primary es claro y el blanco daba 2,30:1
    color: `${theme.palette.primary.contrastText} !important`
  },
  '& .MuiTab-root': {
    minWidth: 65,
    minHeight: 38,
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.up('sm')]: {
      minWidth: 130
    }
  }
}))

// ** Custom Vars
const defaultTier = 12

const infoData = [
  {
    slot: 1,
    key: 'angelDust',
    desc: 'Angel Dust Calculator',
    info: { al: 0, aq: 0, tier: 0, dust: 0, missing: 0 },
    conf: {
      angelTiers: [
        { l: 13, dust: 92 },
        { l: 12, dust: 78 },
        { l: 11, dust: 66 },
        { l: 10, dust: 56 },
        { l: 9, dust: 47 },
        { l: 8, dust: 39 },
        { l: 7, dust: 31 },
        { l: 6, dust: 23 },
        { l: 5, dust: 15 }
      ]
    }
  },
  {
    slot: 2,
    key: 'staminaCalculator',
    desc: 'Stamina Pills Calculator',
    info: { bottle20: 0, bottle50: 0 },
    conf: {
  monsters: [
    {"lvl": 1, "points": 30},
    {"lvl": 2, "points": 60},
    {"lvl": 3, "points": 120},
    {"lvl": 4, "points": 200},
    {"lvl": 5, "points": 300},
    {"lvl": 6, "points": 420},
    {"lvl": 7, "points": 560},
    {"lvl": 8, "points": 720},
    {"lvl": 9, "points": 900},
    {"lvl": 10, "points": 1100},
    {"lvl": 11, "points": 1320},
    {"lvl": 12, "points": 1560},
    {"lvl": 13, "points": 1820},
    {"lvl": 14, "points": 2100},
    {"lvl": 15, "points": 2400},
    {"lvl": 16, "points": 2720},
    {"lvl": 17, "points": 3060},
    {"lvl": 18, "points": 3420},
    {"lvl": 19, "points": 3820},
    {"lvl": 20, "points": 4260},
    {"lvl": 21, "points": 4740},
    {"lvl": 22, "points": 5260},
    {"lvl": 23, "points": 5820},
    {"lvl": 24, "points": 6420},
    {"lvl": 25, "points": 7120},
    {"lvl": 26, "points": 7720},
    {"lvl": 27, "points": 8320},
    {"lvl": 28, "points": 8920},
    {"lvl": 29, "points": 9520},
    {"lvl": 30, "points": 10120},
    {"lvl": 31, "points": 10727},
    {"lvl": 32, "points": 11317},
    {"lvl": 33, "points": 11883},
    {"lvl": 34, "points": 12418},
    {"lvl": 35, "points": 12915}
  ]}
}
]

 
const Minitools = () => {
  // ** State
  const [activeTab, setActiveTab] = useState('staminaCalculator')
  const [isLoading, setIsLoading] = useState(false)
  const hideText = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const [snackOpen, setSnackOpen] = useState({ open: false, type: '' })
  const [mtData, setMtData] = useState([...infoData])

  // ** Hooks
  useEffect(() => {
    // El enlace compartido gana sobre lo guardado; lo propio sigue en localStorage
    const compartido = desdeEstadoMinimo(leerEstadoDeUrl())
    if (compartido) {
      setMtData(compartido)
      setIsLoading(false)

      return
    }

    let saved
    if (typeof window !== 'undefined') {
      // Perform localStorage action
      saved = JSON.parse(localStorage.getItem('mtData'))
    }

    if (saved) setMtData(saved)
    setIsLoading(false)
  }, [])

  const handleShare = async () => {
    const enlace = construirEnlace(aEstadoMinimo(mtData))
    if (!enlace) return setSnackOpen({ open: 'Could not build the link.', type: 'error', time: 4000 })

    try {
      await navigator.clipboard.writeText(enlace)

      return setSnackOpen({ open: 'Link copied — paste it anywhere.', type: 'success', time: 4000 })
    } catch (e) {
      window.history.replaceState(null, '', enlace)

      return setSnackOpen({ open: 'Link is now in the address bar — copy it from there.', type: 'info', time: 6000 })
    }
  }

  const handleSaveData = e => {
    localStorage.setItem('mtData', JSON.stringify(mtData))
    setSnackOpen({ open: `Information Saved.`, type: 'success' })
  }

  const handleRemoveItems = e => {
    setMtData([...infoData])
    localStorage.removeItem('mtData')
    setSnackOpen({ open: `Information Cleared.`, type: 'error' })
  }

  const handleUserConfig = (value, tool, input) => {
    let newMtDataConf
    let mtDataTool = mtData?.find(t => t.key === tool)

    if (input === 'angelLimit') newMtDataConf = { ...mtDataTool.info, al: value }
    if (input === 'angelQty') newMtDataConf = { ...mtDataTool.info, aq: value }
    if (input === 'angelMissing') newMtDataConf = { ...mtDataTool.info, missing: value }
    if (input === 'angelTier') newMtDataConf = { ...mtDataTool.info, tier: value }
    if (input === 'bottle20') newMtDataConf = { ...mtDataTool.info, bottle20: value }
    if (input === 'bottle50') newMtDataConf = { ...mtDataTool.info, bottle50: value }

    if (mtDataTool.info.tier === 0) newMtDataConf = { ...mtDataTool.info, tier: defaultTier }

    const newMtData = mtData.map(t => (t.key === tool ? { ...mtDataTool, info: newMtDataConf } : t))

    setMtData(newMtData)
  }

  const handleChange = (event, value) => {
    setActiveTab(value)
  }

  const snackClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackOpen({ open: false })
  }

  const tabContentList = {
    angelDust: (
      <>
        <Card>
          <Box sx={{ flexGrow: 1 }}>
            <Snackbar
              open={snackOpen.open ? true : false}
              autoHideDuration={snackOpen.time ?? 7000}
              onClose={snackClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert variant='filled' onClose={snackClose} severity={snackOpen.type}>
                <AlertTitle>{snackOpen.type === 'success' ? 'Done!' : 'Info:'}</AlertTitle>
                {snackOpen.open}
              </Alert>
            </Snackbar>
            <Grid container spacing={3} sx={{ minWidth: '320px', padding: '1rem' }}>
              <Grid size={{ xs: 12 }}key={'header-totem'}>
                <Typography
                  variant='h5' component='p'
                  color='primary'
                  align='center'
                  sx={{ marginBottom: '10px', marginTop: '10px' }}
                >
                  {'ANGEL DUST CALCULATOR'}
                </Typography>
                <Divider sx={{ marginBottom: '5px' }} />
                <Box>
                  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                    <Tooltip describeChild title={'Save current configuration.'}>
                      <GreenButton
                        edge='start'

                        variant='contained'
                        startIcon={<Save />}
                        onClick={handleSaveData}
                      >
                        SAVE
                      </GreenButton>
                    </Tooltip>
                      <Button variant='outlined' startIcon={<Icon icon='ic:baseline-link' />} onClick={handleShare}>
                        SHARE
                      </Button>
                    <Divider sx={{ marginLeft: '5px' }} />

                    <Tooltip describeChild title={'Clear all the inputs.'}>
                      <RedButton
                        edge='start'

                        variant='contained'
                        startIcon={<Clear />}
                        onClick={handleRemoveItems}
                      >
                        CLEAR INFO
                      </RedButton>
                    </Tooltip>
                  </Box>
                  <Divider sx={{ marginBottom: '10px', marginTop: '10px' }} />
                  <Box sx={{ marginLeft: '15px' }}>
                    <CustomBox>
                      <Box
                        sx={{
                          minWidth: '200px'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='left'>
                          Max Angel Limit:
                        </Typography>
                      </Box>

                      <TextField
                        slotProps={{ htmlInput: { 'aria-label': 'Max Angel Limit' } }}
                        id='angels-limit'
                        size='small'
                        variant='outlined'
                        type='number'
                        inputProps={{
                          min: 0,
                          max: 150000
                        }}
                        value={
                          mtData?.find(d => d.key === activeTab)?.info?.al > 0
                            ? mtData?.find(d => d.key === activeTab)?.info?.al
                            : ''
                        }
                        onChange={e => {
                          const value = +e.target.value >= 155000 ? 155000 : +e.target.value
                          handleUserConfig(value, activeTab, 'angelLimit')
                        }}
                      />
                    </CustomBox>

                    <Box sx={{ marginBottom: '8px' }} />

                    <CustomBox>
                      <Box
                        sx={{
                          minWidth: '200px'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='left'>
                          Current Angel Quantity:
                        </Typography>
                      </Box>

                      <TextField
                        slotProps={{ htmlInput: { 'aria-label': 'Current Angel Quantity' } }}
                        id='angels-quantity-limit'
                        size='small'
                        variant='outlined'
                        type='number'
                        disabled={mtData?.find(d => d.key === activeTab)?.info?.al > 0 ? false : true}
                        value={
                          mtData?.find(d => d.key === activeTab)?.info?.aq > 0
                            ? mtData?.find(d => d.key === activeTab)?.info?.aq
                            : ''
                        }
                        inputProps={{
                          min: 0,
                          max: 155000
                        }}
                        onChange={e => {
                          const value =
                            +e.target.value >= mtData?.find(d => d.key === activeTab)?.info?.al
                              ? mtData?.find(d => d.key === activeTab)?.info?.al
                              : +e.target.value
                          handleUserConfig(value, activeTab, 'angelQty')
                        }}
                      />
                    </CustomBox>
                    <Box sx={{ marginBottom: '8px' }}></Box>

                    <CustomBox>
                      <Box
                        sx={{
                          minWidth: '200px'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='left'>
                          Angel Tier:
                        </Typography>
                      </Box>

                      <TextField
                        defaultValue={12}
                        align='center'
                        label='Tier'
                        id='load-tier-angel-select'
                        size='small'
                        variant='outlined'
                        value={
                          mtData?.find(d => d.key === activeTab)?.info?.tier > 0
                            ? mtData?.find(d => d.key === activeTab)?.info?.tier
                            : defaultTier
                        }
                        select
                        onChange={e => {
                          const value = +e.target.value
                          handleUserConfig(value, activeTab, 'angelTier')
                        }}
                      >
                        {mtData
                          .find(d => d.key === activeTab)
                          ?.conf?.angelTiers?.map(option => (
                            <MenuItem key={option.l} value={option.l}>
                              {option.l}
                            </MenuItem>
                          ))}
                      </TextField>
                    </CustomBox>
                    <Box sx={{ marginBottom: '8px' }} />

                    <CustomBox>
                      <Box
                        sx={{
                          minWidth: '200px'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='left'>
                          How many do you need?
                        </Typography>
                      </Box>

                      <TextField
                        id='angels-missing-limit'
                        size='small'
                        variant='outlined'
                        type='number'
                        inputProps={{
                          min: 0,
                          max: 155000
                        }}
                        defaultValue={
                          mtData?.find(d => d.key === activeTab)?.info?.al > 0
                            ? mtData?.find(d => d.key === activeTab)?.info?.al -
                              mtData?.find(d => d.key === activeTab)?.info?.aq
                            : null
                        }
                        value={
                          mtData?.find(d => d.key === activeTab)?.info?.missing > 0
                            ? mtData?.find(d => d.key === activeTab)?.info?.missing
                            : mtData?.find(d => d.key === activeTab)?.info?.al -
                              mtData?.find(d => d.key === activeTab)?.info?.aq
                        }
                        onChange={e => {
                          const value =
                            +e.target.value >=
                            mtData?.find(d => d.key === activeTab)?.info?.al -
                              mtData?.find(d => d.key === activeTab)?.info?.aq
                              ? mtData?.find(d => d.key === activeTab)?.info?.al -
                                mtData?.find(d => d.key === activeTab)?.info?.aq
                              : +e.target.value
                          handleUserConfig(value, activeTab, 'angelMissing')
                        }}
                      />
                    </CustomBox>
                  </Box>
                  <Divider sx={{ marginBottom: '10px', marginTop: '10px' }} />

                  <Box sx={{ marginLeft: '15px' }}>
                    <CustomBox>
                      <Box
                        sx={{
                          minWidth: '200px'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='left'>
                          Total Angel Dust Needed:
                        </Typography>
                      </Box>
                      <Typography variant='h5' component='p' color='textPrimary' align='left'>
                        {(mtData?.find(d => d.key === activeTab)?.info?.missing > 0
                          ? mtData
                              ?.find(d => d.key === activeTab)
                              ?.conf?.angelTiers?.find(a => a.l === mtData?.find(d => d.key === activeTab)?.info?.tier)
                              ?.dust * mtData?.find(d => d.key === activeTab)?.info?.missing
                          : 0
                        ).toLocaleString()}
                      </Typography>
                    </CustomBox>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Button
                        variant='contained'
                        startIcon={<Refresh />}
                        sx={{ marginLeft: '10px' }}
                        disabled={
                          mtData?.find(d => d.key === activeTab)?.info?.al > 0 &&
                          mtData?.find(d => d.key === activeTab)?.info?.aq > 0
                            ? false
                            : true
                        }
                        onClick={() => {
                          const missing =
                            mtData?.find(d => d.key === activeTab)?.info?.al -
                            mtData?.find(d => d.key === activeTab)?.info?.aq
                          handleUserConfig(missing, activeTab, 'angelMissing')
                        }}
                      >
                        REFRESH
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </>
    ),
    staminaCalculator: (
      <>
        <Card>
          <Box sx={{ flexGrow: 1 }}>
            <Snackbar
              open={snackOpen.open ? true : false}
              autoHideDuration={snackOpen.time ?? 7000}
              onClose={snackClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert variant='filled' onClose={snackClose} severity={snackOpen.type}>
                <AlertTitle>{snackOpen.type === 'success' ? 'Done!' : 'Info:'}</AlertTitle>
                {snackOpen.open}
              </Alert>
            </Snackbar>
            <Grid container spacing={3} sx={{ minWidth: '320px', padding: '1rem' }}>
              <Grid size={{ xs: 12 }}key={'header-totem'}>
                <Typography
                  variant='h5' component='p'
                  color='primary'
                  align='center'
                  sx={{ marginBottom: '10px', marginTop: '10px' }}
                >
                  {'STAMINA PILLS CALCULATOR'}
                </Typography>
                <Typography
                  variant='body'
                  color='primary'
                  align='center'
                  sx={{ marginBottom: '10px', marginTop: '10px' }}
                >
                  {'With love from ༻Sepж༺'}
                </Typography>
                <Divider sx={{ marginBottom: '5px' }} />
                <Box>
                  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                    <Tooltip describeChild title={'Save current configuration.'}>
                      <GreenButton
                        edge='start'
                        variant='contained'
                        startIcon={<Save />}
                        onClick={handleSaveData}
                      >
                        SAVE
                      </GreenButton>
                    </Tooltip>
                    <Button variant='outlined' startIcon={<Icon icon='ic:baseline-link' />} onClick={handleShare}>
                      SHARE
                    </Button>
                    <Divider sx={{ marginLeft: '5px' }} />

                    <Tooltip describeChild title={'Clear all the inputs.'}>
                      <RedButton
                        edge='start'
                        variant='contained'
                        startIcon={<Clear />}
                        onClick={handleRemoveItems}
                      >
                        CLEAR INFO
                      </RedButton>
                    </Tooltip>
                  </Box>
                  <Divider sx={{ marginBottom: '10px', marginTop: '10px' }} />
                  <Box sx={{ marginLeft: '15px' }}>
                    <CustomBox>
                      <Box
                        sx={{
                          minWidth: '200px'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='left'>
                          x20 Bottle Qty Limit:
                        </Typography>
                      </Box>

                      <TextField
                        slotProps={{ htmlInput: { 'aria-label': 'x20 Bottle Qty Limit' } }}
                        id='angels-limit'
                        size='small'
                        variant='outlined'
                        type='number'
                        value={
                          mtData?.find(d => d.key === activeTab)?.info?.bottle20 > 0
                            ? mtData?.find(d => d.key === activeTab)?.info?.bottle20
                            : ''
                        }
                        onChange={e => {
                          const value = +e.target.value >= 9999 ? 9999 : +e.target.value
                          handleUserConfig(value, activeTab, 'bottle20')
                        }}
                      />
                      <Typography
                        variant='body'
                        color='primary'
                        align='center'
                        sx={{ marginBottom: '10px', marginTop: '10px',marginLeft: '10px' }}
                      >
                        {(mtData?.find(d => d.key === activeTab)?.info?.bottle20 * 20).toLocaleString()}
                      </Typography>
                    </CustomBox>

                    <Box sx={{ marginBottom: '8px' }} />

                    <CustomBox>
                      <Box
                        sx={{
                          minWidth: '200px'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='left'>
                          x50 Bottle Qty Limit:
                        </Typography>
                      </Box>

                      <TextField
                        slotProps={{ htmlInput: { 'aria-label': 'x50 Bottle Qty Limit' } }}
                        id='angels-limit'
                        size='small'
                        variant='outlined'
                        type='number'
                        value={
                          mtData?.find(d => d.key === activeTab)?.info?.bottle50 > 0
                            ? mtData?.find(d => d.key === activeTab)?.info?.bottle50
                            : ''
                        }
                        onChange={e => {
                          const value = +e.target.value >= 9999 ? 9999 : +e.target.value
                          handleUserConfig(value, activeTab, 'bottle50')
                        }}
                      />
                      <Typography
                        variant='body'
                        color='primary'
                        align='center'
                        sx={{ marginBottom: '10px', marginTop: '10px',marginLeft: '10px' }}
                      >
                        {(mtData?.find(d => d.key === activeTab)?.info?.bottle50 * 50).toLocaleString()}
                      </Typography>
                    </CustomBox>

                    <Box sx={{ marginBottom: '8px' }}></Box>

                  </Box>
                  <Divider sx={{ marginBottom: '10px', marginTop: '10px' }} />

                  <Box sx={{ marginLeft: '15px' }}>
                    <CustomBox>
                      <Box
                        sx={{
                          minWidth: '200px'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='left'>
                          Total Stamina Pills:
                        </Typography>
                      </Box>
                      <Typography variant='h5' component='p' color='textPrimary' align='left'>
                        {(mtData?.find(d => d.key === activeTab)?.info?.bottle20 * 20 + mtData?.find(d => d.key === activeTab)?.info?.bottle50 * 50).toLocaleString()}
                      </Typography>
                    </CustomBox>

                    <Divider sx={{ marginBottom: '10px', marginTop: '10px' }} />


                  </Box>

                  <Box sx={{ marginLeft: '15px' }}>
                    <CustomBox>
                      <Box
                        sx={{
                          minWidth: '200px'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='left'>
                          Points for Events:
                        </Typography>
                      </Box>

                    </CustomBox>
                    <Box>
                                <TableContainer>
                                  <Table
                                    key={`points-table-${activeTab}`}
                                    aria-labelledby='tableTitle'
                                    size={'small'}
                                    aria-label='enhanced table'
                                  >
                                    <TableBody>
                                      {/* HEADER */}
                                      {/* component='th': la fila de rotulos vivia dentro del TableBody como <td>, y sin cabeceras las celdas de datos no tienen a que referirse (audit td-has-header) */}
                                      <TableRow>
                                        <TableCell component='th' scope='col' align='center'>{`Level`}</TableCell>
                                        <TableCell component='th' scope='col' align='center'>{`Points`}</TableCell>
                                        <TableCell component='th' scope='col' align='center'>{`Total Points`}</TableCell>
                                      </TableRow>

                                      {/* DETAILS */}
                                      {mtData?.find(d => d.key === activeTab)?.conf?.monsters?.map(row => {
                                        let totalQuantity = row?.points * (mtData?.find(d => d.key === activeTab)?.info?.bottle20 * 20 + mtData?.find(d => d.key === activeTab)?.info?.bottle50 * 50)

                                        return (
                                          <>
                                            <TableRow>
                                              <TableCell align='center'>
                                                {
                                                  <Typography variant='body2' color='textPrimary'>
                                                    {row?.lvl}
                                                  </Typography>
                                                }
                                              </TableCell>
                                              <TableCell align='center'>
                                                {
                                                  <Typography variant='body2' color='textPrimary'>
                                                    {row?.points.toLocaleString()}
                                                  </Typography>
                                                }
                                              </TableCell>
                                              <TableCell align='center'>
                                                {
                                                  <Typography variant='body2' color='textPrimary'>
                                                    {typeof totalQuantity === 'number'
                                                      ? totalQuantity.toLocaleString()
                                                      : ''}
                                                  </Typography>
                                                }
                                              </TableCell>


                                            </TableRow>
                                          </>
                                        )
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>

                    <Divider sx={{ marginBottom: '10px', marginTop: '10px' }} />


                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </>
    )
  }

  return (
    <>
      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <CustomHeader icon='minitools' title='MINITOOLS' />
            <Divider sx={{ marginY: '1rem' }} />
            <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1rem', color: 'primary.main' }}>
              Mini Tools
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              A collection of focused utility calculators for common War and Order planning needs. Each mini tool
              is designed to answer one specific question quickly — without the complexity of the larger tools.
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Includes calculators for Angel capacity, stamina usage, and other in-game resources. Enter your values
              and get an instant answer. All inputs are saved locally in your browser.
            </Typography>
          </Card>
        </Grid>

        {activeTab === undefined ? (
          ''
        ) : (
          <Grid size={{ xs: 12 }}>
            <TabContext value={activeTab}>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12 }}>
                  <TabList
                    variant='scrollable'
                    scrollButtons='auto'
                    onChange={handleChange}
                    aria-label='customized tabs'
                  >
                    <Tab
                      aria-label='Stamina Pills'
                      value='staminaCalculator'
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', ...(!hideText && { '& svg': { mr: 2 } }) }}>
                          <Icon fontSize={20} icon='game-icons:medicines' />
                          {'Stamina Pills'}
                        </Box>
                      }
                    />
                    <Tab
                      aria-label='Angel Dust'
                      value='angelDust'
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', ...(!hideText && { '& svg': { mr: 2 } }) }}>
                          <Icon fontSize={20} icon='game-icons:angel-outfit' />
                          {'Angel Dust'}
                        </Box>
                      }
                    />
                  </TabList>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ pt: theme => `${theme.spacing(4)} !important` }}>
                  {isLoading ? (
                    <Box sx={{ mt: 6, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      <CircularProgress sx={{ mb: 4 }} />
                      <Typography>Loading...</Typography>
                    </Box>
                  ) : (
                    <TabPanel sx={{ p: 0 }} value={activeTab}>
                      {tabContentList[activeTab]}
                    </TabPanel>
                  )}
                </Grid>
              </Grid>
            </TabContext>
          </Grid>
        )}
      </Grid>
    </>
  )
}

Minitools.noLoadingGate = true

export default Minitools
